import React, { useCallback, useEffect, useState } from "react";
import Layout from "../components/Layout";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Trash2, ShieldCheck, User } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useAuth } from "../context/AuthContext";

export default function Users() {
  const { user: me } = useAuth();
  const [items, setItems] = useState([]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/users");
      setItems(data);
    } catch (err) {
      console.error("users load failed", err);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setRole = async (uid, role) => {
    try {
      await api.put(`/users/${uid}/role`, { role });
      toast.success("Role updated");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const del = async (uid) => {
    if (!window.confirm("Delete this operator?")) return;
    try {
      await api.delete(`/users/${uid}`);
      toast.success("Operator removed");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <Layout title="User Policies" subtitle="ROLE-BASED ACCESS">
      <div className="text-xs font-mono text-zinc-500 uppercase tracking-widest mb-3" data-testid="users-count">
        {items.length} OPERATORS · {items.filter((u) => u.role === "admin").length} ADMINS
      </div>
      <div className="border border-zinc-800 rounded-sm bg-zinc-900/40 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 bg-zinc-900/60 border-b border-zinc-800">
            <tr>
              <th className="text-left px-4 py-3">Operator</th>
              <th className="text-left px-4 py-3">Email</th>
              <th className="text-left px-4 py-3">Auth</th>
              <th className="text-left px-4 py-3">Role</th>
              <th className="text-left px-4 py-3">Created</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody data-testid="users-table">
            {items.map((u) => (
              <tr key={u.user_id} className="border-b border-zinc-800/70 hover:bg-zinc-900/60" data-testid={`user-row-${u.user_id}`}>
                <td className="px-4 py-3 flex items-center gap-3">
                  {u.picture ? (
                    <img src={u.picture} alt="" className="w-7 h-7 rounded-sm border border-zinc-800" />
                  ) : (
                    <div className="w-7 h-7 rounded-sm border border-zinc-800 bg-zinc-900 grid place-items-center">
                      {u.role === "admin" ? <ShieldCheck className="w-3.5 h-3.5 text-cyan-400" /> : <User className="w-3.5 h-3.5 text-zinc-400" />}
                    </div>
                  )}
                  <span className="text-zinc-200">{u.name}</span>
                </td>
                <td className="px-4 py-3 font-mono text-xs text-zinc-300">{u.email}</td>
                <td className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-zinc-400">{u.auth_provider}</td>
                <td className="px-4 py-3 w-44">
                  <Select value={u.role} onValueChange={(v) => setRole(u.user_id, v)}>
                    <SelectTrigger data-testid={`role-select-${u.user_id}`} className="bg-zinc-900 border-zinc-800 rounded-sm font-mono uppercase text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-950 border-zinc-800">
                      {["admin", "analyst"].map((o) => <SelectItem key={o} value={o} className="font-mono uppercase text-xs">{o}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </td>
                <td className="px-4 py-3 font-mono text-[11px] text-zinc-500">{u.created_at?.slice(0, 19).replace("T", " ")}</td>
                <td className="px-4 py-3 text-right">
                  {u.user_id !== me?.user_id && (
                    <Button size="sm" variant="ghost" data-testid={`delete-user-${u.user_id}`} onClick={() => del(u.user_id)} className="text-red-400 hover:text-red-300 gap-1.5">
                      <Trash2 className="w-3.5 h-3.5" /> Remove
                    </Button>
                  )}
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={6} className="text-center text-zinc-500 py-8 font-mono">no operators.</td></tr>}
          </tbody>
        </table>
      </div>
    </Layout>
  );
}
